
import { getSpecialNeeds } from '@dataGrid/components/cellRenderer';
import { getDriverStatus } from "../../../modules/Dispatch/RouteExecution/utils/DriverStatus";
import {
  getPickupClosingSoonText,
  getPickupTimeToCloseInMins,
  formatDeliveryOrServiceWindow
} from '../../../modules/Dispatch/utils/utils';

const getNextStop = (stops) => {
  return stops.slice()
    .sort((stop1, stop2) => stop1.stopSequence - stop2.stopSequence)
    .find((stop) => !stop.isStopCompleted);
};

const getDeliveryStopContact = (stopData) => {
  return {
    name: stopData?.customerContactName ?? "",
    phoneNumber: stopData?.phoneNumber === "0" ? "" : stopData?.phoneNumber ?? "",
  };
};

const getPickupStopContact = (stopData) => {
  return {
    name: stopData?.shipperContactName ?? "",
    phoneNumber: stopData?.phoneNumber === "0" ? "" : stopData?.phoneNumber ?? "",
  };
};

const getDeliveryContent = (stopData) => {
  return [
    {
      labels: ["Spl Needs"],
      hasIconsListToRender: true,
      values: getSpecialNeeds({data: stopData}),
    },
    {
      labels: ["Bills"],
      values: [stopData.numberOfBills],
    },
    {
      labels: ["HU", "Weight"],
      values: [stopData.hu, stopData.weight ? `${stopData.weight} lbs` : "N/A"],
    },
    {
      labels: ["APT/Delivery Window"],
      values: [formatDeliveryOrServiceWindow(stopData.deliveryWindow)],
    },
    {
      labels: ["ETA", "ETD"],
      values: [stopData.arrivalTime, stopData.departureTime],
    },
  ];
};

const getPickupContent = (stopData) => {
  return [
    {
      labels: ["Spl Needs"],
      hasIconsListToRender: true,
      values: getSpecialNeeds({data: stopData}),
    },
    {
      labels: ["HU", "Weight"],
      values: [stopData.hu, stopData.weight ? `${stopData.weight} lbs` : "N/A"],
    },
    {
      labels: ["Pieces"],
      values: [stopData.pieces],
    },
    {
      labels: ["Ready", "Close Time"],
      values: [stopData.readyTime, stopData.closeTime],
      isReadyCloseTimeValues : true,
      closeTimeIndex : 1
    },
    {
      labels: ["ETA", "ETD"],
      values: [stopData.arrivalTime, stopData.departureTime],
    },
  ];
};

const getTrailerContent = (trailerData, showTrailerScac) => {
  return [
    {
      labels: ["Trailer#"],
      values: [trailerData.trailerNumber],
    },
    ...(showTrailerScac
      ? [
          {
            labels: ["Trailer Scac"],
            values: [trailerData.trailerScac],
          },
        ]
      : []),
    {
      labels: ["Trailer Type"],
      values: [trailerData.trailerName],
    },
    {
      labels: ["Status"],
      values: [trailerData.trailerStatus],
    },
    {
      labels: ["Spotted by"],
      values: [`${trailerData.driverName} ${trailerData.driverNumber}`],
    },
    {
      labels: ["Spotted since"],
      values: [trailerData.spottedSince],
    },
  ];
};

const getDriverDetails = (routesData,  terminalOffset) => {
    const { driverStatus, driverStatusLabel } = getDriverStatus(
      { data: routesData, context: terminalOffset },
      false,
    );
    const stop = getNextStop(routesData.stops);
    return {
      driverName: routesData.driverName,
      driverNumber: routesData.driverNumber,
      driverId: routesData.driverId,
      nextStopDetails: stop
        ? {
            stopSequence: stop.stopSequence,
            stopName: stop.name,
            stopType: stop.stopType,
          }
        : null,
      driverStatusDetails: {
        driverStatus,
        driverStatusLabel,
      },
    };
  }

const isClosingSoonWarning = (timeToCloseInMins, closingSoonLimitConfig) => {
  return (
    timeToCloseInMins > closingSoonLimitConfig?.closingSoonAlertTimeLimit &&
    timeToCloseInMins <= closingSoonLimitConfig?.closingSoonWarningTimeLimit
  );
};

const isClosingSoonAlert = (timeToCloseInMins, closingSoonLimitConfig) => {
  return timeToCloseInMins <= closingSoonLimitConfig?.closingSoonAlertTimeLimit;
};

const getClosingSoonTextClasses = (timeToCloseInMins,closingSoonLimitConfig) => {
  if (isClosingSoonWarning(timeToCloseInMins,closingSoonLimitConfig)) {
    return "closingSoonWarningText";
  }
  if (isClosingSoonAlert(timeToCloseInMins, closingSoonLimitConfig)) {
    return 'closingSoonAlertText';
  }
};

const getClosingSoonDetails = ({
  isActivePickup,
  closeTime,
  terminalOffset,
  closingSoonLimitConfig
}) => {
  const closingSoonTime = isActivePickup
    ? getPickupTimeToCloseInMins({
        closeTime,
        terminalOffset,
        closingSoonWarningTimeLimit: closingSoonLimitConfig?.closingSoonWarningTimeLimit
      })
    : null;
  return closingSoonTime !== null
    ? {
        closingSoonTextClasses: getClosingSoonTextClasses(closingSoonTime, closingSoonLimitConfig),
        closingSoonTime,
        closingSoonText: getPickupClosingSoonText(closingSoonTime)
      }
    : {
        closingSoonTextClasses: '',
        closingSoonTime: null,
        closingSoonText: null
      };
};

export {
  getNextStop,
  getDeliveryStopContact,
  getPickupStopContact,
  getPickupContent,
  getDeliveryContent,
  getDriverDetails,
  getTrailerContent,
  getClosingSoonTextClasses,
  getClosingSoonDetails,
  isClosingSoonWarning,
  isClosingSoonAlert
};
